import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { useDebounced } from '@/hooks/useDebounced';
import { usePlatformUserSearch, USER_SEARCH_MIN_CHARS } from '@/hooks/usePlatformAdmin';
import { translateApiError } from '@/lib/apiError';
import type { PlatformUser } from '@/types/api';

interface UserSearchProps {
  query: string;
  onQueryChange: (value: string) => void;
  /** What to render on the right of a result row — the caller's action. */
  action: (user: PlatformUser) => ReactNode;
  label: string;
  placeholder: string;
}

/**
 * Find-a-person by email or name, plus whatever the caller wants to do with the
 * one they find.
 *
 * Shared by the password screen and the super-admin screen because both are the
 * same interaction with a different verb; the alternative was two copies of the
 * minimum-length rule, the debounce and the four render states, which is exactly
 * how the two screens end up disagreeing about what "no results" looks like.
 *
 * Below `USER_SEARCH_MIN_CHARS` no request is made at all: the server refuses a
 * shorter query with a 400, and a hint is a better answer than an error the
 * operator has to interpret.
 */
export function UserSearch({
  query,
  onQueryChange,
  action,
  label,
  placeholder,
}: UserSearchProps) {
  const { t } = useTranslation();
  const debounced = useDebounced(query.trim(), 250);
  const { data, isFetching, error } = usePlatformUserSearch(debounced);

  const tooShort = debounced.length < USER_SEARCH_MIN_CHARS;

  return (
    <div className="flex flex-col gap-3">
      <Input
        value={query}
        onChange={(e) => onQueryChange(e.target.value)}
        placeholder={placeholder}
        aria-label={label}
        autoComplete="off"
      />

      {tooShort ? (
        <p className="text-[12.5px] text-muted">
          {t('platformAdmin.users.minChars', { count: USER_SEARCH_MIN_CHARS })}
        </p>
      ) : error ? (
        <p role="alert" className="text-[12.5px] text-destructive">
          {translateApiError(error, { fallback: t('platformAdmin.users.searchFailed') })}
        </p>
      ) : isFetching && !data ? (
        <p className="text-[12.5px] text-muted">{t('platformAdmin.loading')}</p>
      ) : !data || data.length === 0 ? (
        <p className="text-[12.5px] text-muted">{t('platformAdmin.users.noMatches')}</p>
      ) : (
        <Card className="overflow-hidden p-0">
          <ul className="divide-y divide-line">
            {data.map((user) => (
              <li key={user.id} className="flex items-center gap-3 px-4 py-3">
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[14px] font-medium tracking-[-0.01em]">
                    {user.name}
                  </div>
                  <div className="truncate text-[12.5px] text-muted">{user.email}</div>
                </div>
                {action(user)}
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}
